import { Component, inject, input, InputSignal, ViewChild } from '@angular/core';
import { MatMenu, MatMenuItem } from '@angular/material/menu';

import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Store } from '@ngrx/store';
import { SprayTypes } from 'src/app/shared/models';

import { MatDivider } from '@angular/material/divider';
import { BlightSignalStore } from '../../signal-store/blight-signal-store';
import { CropFieldAdvice } from '../../../shared/components/disease-overview-table.component/models/models';
import { BlightAIIdentifiers } from '../../../blight-v2/shared/constants';
import { AppState } from '../../../shared/models';

@Component({
  selector: 'app-blight-action-menu',
  template: `
    <mat-menu #actionMenu="matMenu">
      <button mat-menu-item (click)="onSprayClicked()">
        {{ 'blightV2.actionMenu.spray' | translate }}
      </button>
      <mat-divider />
      <button mat-menu-item (click)="onIrrigateClicked()">
        {{ 'blightV2.actionMenu.irrigation' | translate }}
      </button>
    </mat-menu>
  `,
  imports: [MatMenu, MatMenuItem, MatDivider, TranslatePipe],
})
export class ActionMenuComponent {
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly appStore = inject<Store<AppState>>(Store);
  private readonly blightStore = inject(BlightSignalStore);
  private readonly locale = inject(LocaleService);

  @ViewChild('actionMenu', { static: true }) public menu!: MatMenu;

  protected sprayTypes: typeof SprayTypes = SprayTypes;

  readonly advices: InputSignal<CropFieldAdvice[]> = input.required<CropFieldAdvice[]>();
  readonly actionDate: InputSignal<Date | undefined> = input<Date | undefined>();

  protected onSprayClicked(): void {
    const cropYear = this.advices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackSprayEvent();
    this.blightStore.openSpraysDialog(this.advices(), this.actionDate());
  }

  protected onIrrigateClicked(): void {
    const cropYear = this.advices()[0]?.cropYear;
    if (cropYear == null) {
      this.notifyMutationUnavailable();
      return;
    }

    this.trackIrrigationEvent();
    this.blightStore.openBlightIrrigation(this.advices(), this.actionDate());
  }

  private trackSprayEvent(): void {
    this.ai.trackEvent(BlightAIIdentifiers.selectPreventive, {});
  }

  private trackIrrigationEvent(): void {
    this.ai.trackEvent(BlightAIIdentifiers.selectIrrigation, {});
  }

  private notifyMutationUnavailable(): void {
    this.appStore.dispatch(
      NotificationActions.infoSnackbar({
        message: this.locale.translate('UI.CropYearUnavailableMessage'),
      }),
    );
  }
}
